import React, { useState } from 'react';
import { Button, Modal } from '@/components/ui';
import messageBoardService, {
  type MessageBoardPostListItem,
} from '../../../services/messageBoardService';
import {
  formatMessageBoardTime,
  messageBoardTitle,
} from '../utils/formatMessageBoardTime';
import MessageBoardPostAudience from './MessageBoardPostAudience';
import MessageBoardPostProgress from './MessageBoardPostProgress';
import MessageBoardReadReceipts from './MessageBoardReadReceipts';

export interface MessageBoardPostCardProps {
  post: MessageBoardPostListItem;
  /**
   * Optional acknowledgement badge matching the mockup's ACK chip.
   * Default (omitted/false) does not render — there is no real data source yet.
   * TODO: needs a schema field before this can reflect real state.
   */
  requiresAck?: boolean;
  /** Holders of message_board:moderate see the take-down action. */
  canModerate?: boolean;
  /** Holders of message_board:view_reads see who has and has not opened the post. */
  canViewReads?: boolean;
  onRemoved?: () => void;
}

const MessageBoardPostCard: React.FC<MessageBoardPostCardProps> = ({
  post,
  requiresAck = false,
  canModerate = false,
  canViewReads = false,
  onRemoved,
}) => {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const title = messageBoardTitle(post.message);

  const confirmRemove = async () => {
    setRemoving(true);
    setRemoveError(null);
    try {
      await messageBoardService.remove(post.id);
      setConfirming(false);
      onRemoved?.();
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : 'Failed to take down this message');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <details
      className="tw-group tw-border-t tw-border-line first:tw-border-t-0"
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="tw-flex tw-min-h-11 tw-cursor-pointer tw-list-none tw-items-start tw-gap-2 tw-bg-surface tw-px-4 tw-py-3 marker:tw-content-none group-open:tw-sticky group-open:tw-top-0 group-open:tw-z-10 group-open:tw-border-b group-open:tw-border-line [&::-webkit-details-marker]:tw-hidden">
        <div className="tw-min-w-0 tw-flex-1">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
            <h3 className="tw-m-0 tw-min-w-0 tw-truncate tw-text-sm tw-font-semibold tw-text-content">
              {title}
            </h3>
            {/* TODO: needs a schema field before this can reflect real state. */}
            {requiresAck ? (
              <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-rounded tw-bg-content tw-px-1.5 tw-py-0.5 tw-text-[0.65rem] tw-font-semibold tw-text-white">
                ACK
              </span>
            ) : null}
            {post.urgency === 'URGENT' ? (
              <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-text-[0.65rem] tw-font-semibold tw-text-danger">
                Urgent
              </span>
            ) : null}
            {post.urgency === 'URGENT' && post.readCount < post.recipientCount ? (
              <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-rounded tw-bg-danger tw-px-1.5 tw-py-0.5 tw-text-[0.65rem] tw-font-semibold tw-text-white">
                Not All Seen
              </span>
            ) : null}
          </div>
          <p className="tw-mb-0 tw-mt-0.5 tw-text-xs tw-text-content-muted">
            <MessageBoardPostAudience label={`Foreman Area · ${post.foremanArea.name}`} />
            <span aria-hidden="true"> · </span>
            <time dateTime={post.createdAt}>{formatMessageBoardTime(post.createdAt)}</time>
          </p>
          <MessageBoardPostProgress
            readCount={post.readCount}
            recipientCount={post.recipientCount}
            urgency={post.urgency}
          />
        </div>
        <i
          className="bi bi-chevron-down tw-mt-0.5 tw-shrink-0 tw-text-xs tw-text-content-muted tw-transition group-open:tw-rotate-180"
          aria-hidden
        />
      </summary>
      <div className="tw-px-4 tw-pb-3">
        <div className="tw-rounded-lg tw-bg-surface-alt tw-px-3 tw-py-2 tw-whitespace-pre-wrap tw-text-sm tw-text-content-secondary">
          {post.message}
        </div>
        {open && canViewReads ? <MessageBoardReadReceipts postId={post.id} /> : null}
        {canModerate ? (
          <div className="tw-mt-2 tw-flex tw-justify-end">
            <Button
              type="button"
              variant="outline-danger"
              size="sm"
              onClick={() => {
                setRemoveError(null);
                setConfirming(true);
              }}
            >
              Take down
            </Button>
          </div>
        ) : null}
      </div>
      <Modal
        show={confirming}
        onHide={() => {
          if (!removing) setConfirming(false);
        }}
        size="sm"
        mobileVariant="center"
        backdrop={removing ? 'static' : true}
      >
        <Modal.Header closeButton>
          <Modal.Title>Take this message down?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="tw-mb-0">
            Your crew will stop seeing it on the web and mobile boards. The message and who has
            already read it stay on the audit record.
          </p>
          {removeError ? (
            <p className="tw-mb-0 tw-mt-2 tw-text-sm tw-text-danger" role="alert">
              {removeError}
            </p>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirming(false)} disabled={removing}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmRemove} disabled={removing}>
            {removing ? 'Taking down…' : 'Confirm take down'}
          </Button>
        </Modal.Footer>
      </Modal>
    </details>
  );
};

export default MessageBoardPostCard;
